// src/components/Dashboard/ClubCoordinator/PartnerApplications.js
import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
// src/components/Dashboard/ClubCoordinator/PartnerApplications.js  
import { db } from '../../../services/firebase'; // Update this path
import './ClubCoordinator.css';
import jsPDF from 'jspdf';

const PartnerApplications = ({ applications, loading, showTitle = true }) => {
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editApprover, setEditApprover] = useState(false);
  const [approverData, setApproverData] = useState({
    approverTitleFullNames: '',
    approverPosition: 'FMLC Coordinator',
    approverDate: '',
    approverSignature: ''
  });

  useEffect(() => {
    setFilteredApplications(
      applications.filter(app =>
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.businessEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, applications]);

  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setApproverData({
      approverTitleFullNames: application.approverTitleFullNames || '',
      approverPosition: application.approverPosition || 'FMLC Coordinator',
      approverDate: application.approverDate || '',
      approverSignature: application.approverSignature || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
    setEditApprover(false);
  };

  const handleApproverChange = (e) => {
    const { name, value } = e.target;
    setApproverData(prev => ({ ...prev, [name]: value }));
  };

  const saveApproverChanges = async () => {
    try {
      const docRef = doc(db, 'partnerApplications', selectedApplication.id);
      await updateDoc(docRef, approverData);
      
      // Update local state
      const updatedApplications = applications.map(app => 
        app.id === selectedApplication.id ? { ...app, ...approverData } : app
      );
      
      setFilteredApplications(updatedApplications);
      setSelectedApplication(prev => ({ ...prev, ...approverData }));
      setEditApprover(false);
      alert('Approver details updated successfully.');
    } catch (error) {
      console.error('Error updating approver details:', error);
      alert('Failed to update approver details.');
    }
  };

  const escapeString = (input) => {
    if (input == null) return 'N/A';
    return String(input).replace(/[<>&"]/g, '');
  };

  const generatePDFContent = (application) => {
    const doc = new jsPDF();
    let y = 10;

    doc.setFontSize(16);
    doc.text('Partner Application', 10, y);
    y += 10;

    doc.setFontSize(12);
    doc.text('Application Details', 10, y);
    y += 10;

    const addDetail = (label, value) => {
      doc.text(`${label}: ${escapeString(value)}`, 10, y);
      y += 7;
    };

    addDetail('New Application', application.newApplication ? 'Yes' : 'No');
    addDetail('Renewal Application', application.renewalApplication ? 'Yes' : 'No');
    addDetail('Entity Registered Name', application.entityRegisteredName);
    addDetail('Trading Name', application.tradingName);
    addDetail('Business Registration Number', application.businessRegistrationNumber);
    addDetail('Tax Identification Number', application.taxIdentificationNumber);
    addDetail('Director Names', application.directorNames);
    addDetail('Industry Sector', application.industrySector);
    addDetail('Office Contact Number', application.officeContactNumber);
    addDetail('Business Email', application.businessEmail);
    addDetail('Physical Business Location', application.physicalBusinessLocation);
    addDetail('Street Address', application.streetAddress);
    addDetail('District', application.district);
    addDetail('Number Of Employees', application.numberOfEmployees);
    addDetail('Website', application.website);
    addDetail('Social Media Links', application.socialMediaLinks);
    addDetail('Liaison Title Full Names', application.liaisonTitleFullNames);
    addDetail('Liaison Job Position', application.liaisonJobPosition);
    addDetail('Liaison Department', application.liaisonDepartment);
    addDetail('Liaison Contact Number', application.liaisonContactNumber);
    addDetail('Liaison Direct Email', application.liaisonDirectEmail);
    addDetail('Years Of Operation', application.yearsOfOperation);
    addDetail('Target Market', application.targetMarket);
    addDetail('Age Demographics', application.ageDemographics?.join(', ') || 'N/A');
    addDetail('Effective Marketing Channels', application.effectiveMarketingChannels?.join(', ') || 'N/A');
    addDetail('Collaboration Frequency', application.collaborationFrequency);
    addDetail('Co Host Sponsor Mentor', application.coHostSponsorMentor);
    addDetail('Specify How', application.specifyHow?.join(', ') || 'N/A');
    addDetail('Agree Admin Fee', application.agreeAdminFee ? 'Yes' : 'No');
    addDetail('Payment Method', application.paymentMethod);
    addDetail('MPesa Number', application.mPesaNumber);
    addDetail('Eco Cash Number', application.ecoCashNumber);
    addDetail('Account Holder', application.accountHolder);
    addDetail('Bank Name', application.bankName);
    addDetail('Account No', application.accountNo);
    addDetail('Branch Code', application.branchCode);
    addDetail('Authorized Signature', application.authorizedSignature);
    addDetail('Date', application.date);
    addDetail('Personal Id Checked', application.personalIdChecked ? 'Yes' : 'No');
    addDetail('Business Id Checked', application.businessIdChecked ? 'Yes' : 'No');
    addDetail('Business Traders License Checked', application.businessTradersLicenseChecked ? 'Yes' : 'No');
    addDetail('Tax Clearance Certificate Checked', application.taxClearanceCertificateChecked ? 'Yes' : 'No');
    addDetail('Business Utility Bill Checked', application.businessUtilityBillChecked ? 'Yes' : 'No');
    addDetail('Business Sublease Agreement Checked', application.businessSubleaseAgreementChecked ? 'Yes' : 'No');
    addDetail('Applicant Title Full Names', application.applicantTitleFullNames);
    addDetail('Applicant Position', application.applicantPosition);
    addDetail('Applicant Business Name', application.applicantBusinessName);
    addDetail('Applicant Date', application.applicantDate);
    addDetail('Representative Title Full Names', application.representativeTitleFullNames);
    addDetail('Representative Position', application.representativePosition);
    addDetail('Representative Date', application.representativeDate);
    addDetail('Representative Signature', application.representativeSignature);
    addDetail('Approver Title Full Names', application.approverTitleFullNames);
    addDetail('Approver Position', application.approverPosition);
    addDetail('Approver Date', application.approverDate);
    addDetail('Approver Signature', application.approverSignature);
    addDetail('Other Comments', application.otherComments);
    addDetail('Status', application.status);
    addDetail('Submitted At', application.submittedAt ? new Date(application.submittedAt.seconds * 1000).toLocaleString() : 'N/A');

    const checkboxSections = [
      {
        title: 'Business Line of Operation',
        fields: [
          { key: 'groceriesHouseholdEssentials', label: 'Groceries & Household Essentials' },
          { key: 'healthcareWellness', label: 'Healthcare & Wellness' },
          { key: 'clothingPersonalCare', label: 'Clothing & Personal Care' },
          { key: 'businessDevelopmentEntrepreneurship', label: 'Business Development & Entrepreneurship' },
          { key: 'entertainmentLiveEvents', label: 'Entertainment & Live Events' },
          { key: 'housingUtilities', label: 'Housing & Utilities' },
          { key: 'sportsRecreation', label: 'Sports & Recreation' },
          { key: 'educationSkillsDevelopment', label: 'Education & Skills Development' },
          { key: 'tourismArtsCulture', label: 'Tourism, Arts & Culture' },
          { key: 'technologyConnectivity', label: 'Technology & Connectivity' },
          { key: 'travelMobility', label: 'Travel & Mobility' },
          { key: 'lodgingAccommodation', label: 'Lodging and Accommodation' },
          { key: 'industryFacilityTours', label: 'Industry and Facility Tours' },
          { key: 'othersOperation', label: 'Others' }
        ]
      },
      {
        title: 'Business Engagement Areas',
        fields: [
          { key: 'generalManagement', label: 'General Management' },
          { key: 'operations', label: 'Operations' },
          { key: 'finance', label: 'Finance' },
          { key: 'purchasing', label: 'Purchasing' },
          { key: 'marketing', label: 'Marketing' },
          { key: 'informationTechnology', label: 'Information Technology' },
          { key: 'humanResources', label: 'Human Resources' },
          { key: 'customerService', label: 'Customer Service' }
        ]
      },
      {
        title: 'Value-Added Offerings',
        fields: [
          { key: 'productServicePromotions', label: 'Product & Service Promotions' },
          { key: 'outdoorAdventures', label: 'Outdoor Adventures' },
          { key: 'industryFacilityToursValue', label: 'Industry and Facility Tours' },
          { key: 'businessDevelopmentEntrepreneurshipValue', label: 'Business Development & Entrepreneurship' },
          { key: 'entertainmentLiveEventsValue', label: 'Entertainment & Live Events' },
          { key: 'communityEngagementCSR', label: 'Community Engagement & CSR' },
          { key: 'sportsRecreationValue', label: 'Sports & Recreation' },
          { key: 'familyRelationshipsCounselling', label: 'Family, Relationships & Counselling' },
          { key: 'tourismArtsCultureValue', label: 'Tourism, Arts & Culture' },
          { key: 'wellnessHealthRelaxation', label: 'Wellness, Health & Relaxation' },
          { key: 'travelAccommodation', label: 'Travel & Accommodation' },
          { key: 'wealthFinancialPlanning', label: 'Wealth & Financial Planning' }
        ]
      },
      {
        title: 'Supplier Empowerment & Business Support',
        fields: [
          { key: 'clientCustomerSourcing', label: 'Client, customer sourcing' },
          { key: 'marketOpportunitiesSupport', label: 'Market opportunities and support' },
          { key: 'betterPricesDealSuppliers', label: 'Better prices deal from suppliers' },
          { key: 'accessPreferredVendors', label: 'Access to preferred vendors' },
          { key: 'strategicSourcingSupport', label: 'Strategic sourcing support' },
          { key: 'representationSupplierNegotiations', label: 'Representation during supplier negotiations' },
          { key: 'flexibleSupplierPaymentTerms', label: 'Flexible supplier payment terms' },
          { key: 'jointSourcingEfforts', label: 'Joint sourcing efforts with FMLC partners' },
          { key: 'businessDevelopmentServices', label: 'Business development services' },
          { key: 'enterpriseSupportServices', label: 'Enterprise support services provision' }
        ]
      }
    ];

    checkboxSections.forEach(section => {
      const selected = section.fields.filter(f => application[f.key]).map(f => f.label).join(', ');
      if (selected) {
        addDetail(section.title, selected);
      }
    });

    return doc;
  };

  const downloadApplication = (application) => {
    const doc = generatePDFContent(application);
    doc.save(`partner-application-${application.name}.pdf`);
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Contact', 'Approval Date'];
    const csvData = filteredApplications.map(app => [
      app.name,
      app.businessEmail,
      app.officeContactNumber,
      app.coordinatorReviewedAt ? new Date(app.coordinatorReviewedAt.seconds * 1000).toLocaleDateString() : 'N/A'
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field || 'N/A'}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'partner-approved-applications.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="loading">Loading partner applications...</div>;
  }

  return (
    <div className="partner-applications">
      {showTitle && <h3>Approved Partner Applications ({filteredApplications.length})</h3>}
      
      <div className="dashboard-controls">
        <div className="search-control">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="export-buttons">
          <button onClick={exportToCSV} className="btn-primary">
            Export to CSV
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Contact</th>
              <th>Approved Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApplications.map(application => (
              <tr key={`partner-${application.id}`}>
                <td>{application.name}</td>
                <td>{application.businessEmail}</td>
                <td>{application.officeContactNumber}</td>
                <td>
                  {application.coordinatorReviewedAt ?
                    new Date(application.coordinatorReviewedAt.seconds * 1000).toLocaleDateString() :
                    'N/A'
                  }
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-primary"
                      onClick={() => viewApplicationDetails(application)}
                    >
                      View
                    </button>
                    <button
                      className="btn-success"
                      onClick={() => downloadApplication(application)}
                    >
                      Download PDF
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredApplications.length === 0 && (
          <p className="no-data">No approved partner applications found.</p>
        )}
      </div>

      {showModal && selectedApplication && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Partner Application Details</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="application-details">
                <h4>Application Type</h4>
                <div className="detail-row">
                  <span className="detail-label">New Application:</span>
                  <span className="detail-value">{selectedApplication.newApplication ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Renewal Application:</span>
                  <span className="detail-value">{selectedApplication.renewalApplication ? 'Yes' : 'No'}</span>
                </div>
                <h4>Entity Information</h4>
                <div className="detail-row">
                  <span className="detail-label">Entity Registered Name:</span>
                  <span className="detail-value">{selectedApplication.entityRegisteredName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Trading Name:</span>
                  <span className="detail-value">{selectedApplication.tradingName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Business Registration Number:</span>
                  <span className="detail-value">{selectedApplication.businessRegistrationNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Tax Identification Number:</span>
                  <span className="detail-value">{selectedApplication.taxIdentificationNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Director Names:</span>
                  <span className="detail-value">{selectedApplication.directorNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Industry Sector:</span>
                  <span className="detail-value">{selectedApplication.industrySector || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Office Contact Number:</span>
                  <span className="detail-value">{selectedApplication.officeContactNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Business Email:</span>
                  <span className="detail-value">{selectedApplication.businessEmail || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Physical Business Location:</span>
                  <span className="detail-value">{selectedApplication.physicalBusinessLocation || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Street Address:</span>
                  <span className="detail-value">{selectedApplication.streetAddress || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">District:</span>
                  <span className="detail-value">{selectedApplication.district || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Number of Employees:</span>
                  <span className="detail-value">{selectedApplication.numberOfEmployees || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Website:</span>
                  <span className="detail-value">{selectedApplication.website || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Social Media Links:</span>
                  <span className="detail-value">{selectedApplication.socialMediaLinks || 'N/A'}</span>
                </div>
                <h4>Business Liaison Contact</h4>
                <div className="detail-row">
                  <span className="detail-label">Liaison Title Full Names:</span>
                  <span className="detail-value">{selectedApplication.liaisonTitleFullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Liaison Job Position:</span>
                  <span className="detail-value">{selectedApplication.liaisonJobPosition || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Liaison Department:</span>
                  <span className="detail-value">{selectedApplication.liaisonDepartment || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Liaison Contact Number:</span>
                  <span className="detail-value">{selectedApplication.liaisonContactNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Liaison Direct Email:</span>
                  <span className="detail-value">{selectedApplication.liaisonDirectEmail || 'N/A'}</span>
                </div>
                <h4>Business Line of Operation Survey</h4>
                <div className="detail-row">
                  <span className="detail-label">Selected Options:</span>
                  <span className="detail-value">
                    {[
                      {key: 'groceriesHouseholdEssentials', label: 'Groceries & Household Essentials'},
                      {key: 'healthcareWellness', label: 'Healthcare & Wellness'},
                      {key: 'clothingPersonalCare', label: 'Clothing & Personal Care'},
                      {key: 'businessDevelopmentEntrepreneurship', label: 'Business Development & Entrepreneurship'},
                      {key: 'entertainmentLiveEvents', label: 'Entertainment & Live Events'},
                      {key: 'housingUtilities', label: 'Housing & Utilities'},
                      {key: 'sportsRecreation', label: 'Sports & Recreation'},
                      {key: 'educationSkillsDevelopment', label: 'Education & Skills Development'},
                      {key: 'tourismArtsCulture', label: 'Tourism, Arts & Culture'},
                      {key: 'technologyConnectivity', label: 'Technology & Connectivity'},
                      {key: 'travelMobility', label: 'Travel & Mobility'},
                      {key: 'lodgingAccommodation', label: 'Lodging and Accommodation'},
                      {key: 'industryFacilityTours', label: 'Industry and Facility Tours'},
                      {key: 'othersOperation', label: 'Others'}
                    ].filter(f => selectedApplication[f.key]).map(f => f.label).join(', ') || 'N/A'}
                  </span>
                </div>
                <h4>Business Engagement Areas</h4>
                <div className="detail-row">
                  <span className="detail-label">Selected Options:</span>
                  <span className="detail-value">
                    {[
                      {key: 'generalManagement', label: 'General Management'},
                      {key: 'operations', label: 'Operations'},
                      {key: 'finance', label: 'Finance'},
                      {key: 'purchasing', label: 'Purchasing'},
                      {key: 'marketing', label: 'Marketing'},
                      {key: 'informationTechnology', label: 'Information Technology'},
                      {key: 'humanResources', label: 'Human Resources'},
                      {key: 'customerService', label: 'Customer Service'}
                    ].filter(f => selectedApplication[f.key]).map(f => f.label).join(', ') || 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Other Engagement:</span>
                  <span className="detail-value">{selectedApplication.otherEngagement || 'N/A'}</span>
                </div>
                <h4>Value-Added Offerings</h4>
                <div className="detail-row">
                  <span className="detail-label">Selected Options:</span>
                  <span className="detail-value">
                    {[
                      {key: 'productServicePromotions', label: 'Product & Service Promotions'},
                      {key: 'outdoorAdventures', label: 'Outdoor Adventures'},
                      {key: 'industryFacilityToursValue', label: 'Industry and Facility Tours'},
                      {key: 'businessDevelopmentEntrepreneurshipValue', label: 'Business Development & Entrepreneurship'},
                      {key: 'entertainmentLiveEventsValue', label: 'Entertainment & Live Events'},
                      {key: 'communityEngagementCSR', label: 'Community Engagement & CSR'},
                      {key: 'sportsRecreationValue', label: 'Sports & Recreation'},
                      {key: 'familyRelationshipsCounselling', label: 'Family, Relationships & Counselling'},
                      {key: 'tourismArtsCultureValue', label: 'Tourism, Arts & Culture'},
                      {key: 'wellnessHealthRelaxation', label: 'Wellness, Health & Relaxation'},
                      {key: 'travelAccommodation', label: 'Travel & Accommodation'},
                      {key: 'wealthFinancialPlanning', label: 'Wealth & Financial Planning'}
                    ].filter(f => selectedApplication[f.key]).map(f => f.label).join(', ') || 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Other Value Added:</span>
                  <span className="detail-value">{selectedApplication.otherValueAdded || 'N/A'}</span>
                </div>
                <h4>Entity Profile & Engagement</h4>
                <div className="detail-row">
                  <span className="detail-label">Years Of Operation:</span>
                  <span className="detail-value">{selectedApplication.yearsOfOperation || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Target Market:</span>
                  <span className="detail-value">{selectedApplication.targetMarket || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Age Demographics:</span>
                  <span className="detail-value">{selectedApplication.ageDemographics?.join(', ') || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Effective Marketing Channels:</span>
                  <span className="detail-value">{selectedApplication.effectiveMarketingChannels?.join(', ') || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Collaboration Frequency:</span>
                  <span className="detail-value">{selectedApplication.collaborationFrequency || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Co Host Sponsor Mentor:</span>
                  <span className="detail-value">{selectedApplication.coHostSponsorMentor || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Specify How:</span>
                  <span className="detail-value">{selectedApplication.specifyHow?.join(', ') || 'N/A'}</span>
                </div>
                <h4>Supplier Empowerment & Business Support</h4>
                <div className="detail-row">
                  <span className="detail-label">Selected Options:</span>
                  <span className="detail-value">
                    {[
                      {key: 'clientCustomerSourcing', label: 'Client, customer sourcing'},
                      {key: 'marketOpportunitiesSupport', label: 'Market opportunities and support'},
                      {key: 'betterPricesDealSuppliers', label: 'Better prices deal from suppliers'},
                      {key: 'accessPreferredVendors', label: 'Access to preferred vendors'},
                      {key: 'strategicSourcingSupport', label: 'Strategic sourcing support'},
                      {key: 'representationSupplierNegotiations', label: 'Representation during supplier negotiations'},
                      {key: 'flexibleSupplierPaymentTerms', label: 'Flexible supplier payment terms'},
                      {key: 'jointSourcingEfforts', label: 'Joint sourcing efforts with FMLC partners'},
                      {key: 'businessDevelopmentServices', label: 'Business development services'},
                      {key: 'enterpriseSupportServices', label: 'Enterprise support services provision'}
                    ].filter(f => selectedApplication[f.key]).map(f => f.label).join(', ') || 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Other Supplier Empowerment:</span>
                  <span className="detail-value">{selectedApplication.otherSupplierEmpowerment || 'N/A'}</span>
                </div>
                <h4>Administration Fee & Payment Details</h4>
                <div className="detail-row">
                  <span className="detail-label">Agree Admin Fee:</span>
                  <span className="detail-value">{selectedApplication.agreeAdminFee ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value">{selectedApplication.paymentMethod || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">MPesa Number:</span>
                  <span className="detail-value">{selectedApplication.mPesaNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Eco Cash Number:</span>
                  <span className="detail-value">{selectedApplication.ecoCashNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Account Holder:</span>
                  <span className="detail-value">{selectedApplication.accountHolder || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Bank Name:</span>
                  <span className="detail-value">{selectedApplication.bankName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Account No:</span>
                  <span className="detail-value">{selectedApplication.accountNo || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Branch Code:</span>
                  <span className="detail-value">{selectedApplication.branchCode || 'N/A'}</span>
                </div>
                <h4>Agreement & Signature</h4>
                <div className="detail-row">
                  <span className="detail-label">Authorized Signature:</span>
                  <span className="detail-value">{selectedApplication.authorizedSignature || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{selectedApplication.date || 'N/A'}</span>
                </div>
                <h4>Attachments</h4>
                <div className="detail-row">
                  <span className="detail-label">Personal Id Checked:</span>
                  <span className="detail-value">{selectedApplication.personalIdChecked ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Business Id Checked:</span>
                  <span className="detail-value">{selectedApplication.businessIdChecked ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Business Traders License Checked:</span>
                  <span className="detail-value">{selectedApplication.businessTradersLicenseChecked ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Tax Clearance Certificate Checked:</span>
                  <span className="detail-value">{selectedApplication.taxClearanceCertificateChecked ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Business Utility Bill Checked:</span>
                  <span className="detail-value">{selectedApplication.businessUtilityBillChecked ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Business Sublease Agreement Checked:</span>
                  <span className="detail-value">{selectedApplication.businessSubleaseAgreementChecked ? 'Yes' : 'No'}</span>
                </div>
                <h4>Declaration</h4>
                <div className="detail-row">
                  <span className="detail-label">Applicant Title Full Names:</span>
                  <span className="detail-value">{selectedApplication.applicantTitleFullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Applicant Position:</span>
                  <span className="detail-value">{selectedApplication.applicantPosition || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Applicant Business Name:</span>
                  <span className="detail-value">{selectedApplication.applicantBusinessName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Applicant Date:</span>
                  <span className="detail-value">{selectedApplication.applicantDate || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Representative Title Full Names:</span>
                  <span className="detail-value">{selectedApplication.representativeTitleFullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Representative Position:</span>
                  <span className="detail-value">{selectedApplication.representativePosition || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Representative Date:</span>
                  <span className="detail-value">{selectedApplication.representativeDate || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Representative Signature:</span>
                  <span className="detail-value">{selectedApplication.representativeSignature || 'N/A'}</span>
                </div>
                <h4>Approval Information</h4>
                {editApprover ? (
                  <div className="edit-approver-form">
                    <h5>Edit FMLC Approver Details</h5>
                    <div className="form-group">
                      <label>Title & Full Names</label>
                      <input
                        type="text"
                        name="approverTitleFullNames"
                        value={approverData.approverTitleFullNames}
                        onChange={handleApproverChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Position</label>
                      <input
                        type="text"
                        name="approverPosition"
                        value={approverData.approverPosition}
                        onChange={handleApproverChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Date</label>
                      <input
                        type="date"
                        name="approverDate"
                        value={approverData.approverDate}
                        onChange={handleApproverChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Signature</label>
                      <input
                        type="text"
                        name="approverSignature"
                        value={approverData.approverSignature}
                        onChange={handleApproverChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-actions">
                      <button
                        className="btn-primary"
                        onClick={saveApproverChanges}
                      >
                        Save Changes
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => setEditApprover(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="detail-row">
                      <span className="detail-label">Status:</span>
                      <span className="detail-value">
                        <span className={`status-badge status-${selectedApplication.status || 'approved'}`}>
                          {selectedApplication.status || 'Approved'}
                        </span>
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Approved Date:</span>
                      <span className="detail-value">
                        {selectedApplication.coordinatorReviewedAt ?
                          new Date(selectedApplication.coordinatorReviewedAt.seconds * 1000).toLocaleDateString() :
                          'N/A'
                        }
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Approver Name:</span>
                      <span className="detail-value">{selectedApplication.approverTitleFullNames || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Approver Position:</span>
                      <span className="detail-value">{selectedApplication.approverPosition || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Approver Date:</span>
                      <span className="detail-value">{selectedApplication.approverDate || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Approver Signature:</span>
                      <span className="detail-value">{selectedApplication.approverSignature || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Other Comments:</span>
                      <span className="detail-value">{selectedApplication.otherComments || 'N/A'}</span>
                    </div>
                    <button
                      className="btn-primary"
                      onClick={() => setEditApprover(true)}
                    >
                      Edit Approver Details
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-success" onClick={() => downloadApplication(selectedApplication)}>
                Download PDF
              </button>
              <button className="btn-secondary" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerApplications;